<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCurrentLocale } from '@/i18n';
import { getTextVariant } from '@/utils/stringUtils';
import { formatDate } from '@/utils/dateUtils';
import { useViewComposable } from '@/views/composable';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useConvertTextComposable } from '@/components/modal/useConvertTextComposable';
import { useDialog } from 'primevue/usedialog';
import HistoryViewMoreModal from '@/components/modal/HistoryViewMoreModal.vue';

const props = defineProps<{
  data: any;
  isTermConditionTypes?: boolean;
  groupTypes?: any;
  destCode?: any;
}>();

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const dialog = useDialog();
const { handleReplaceValueToName } = useConvertTextComposable();
const { renderListMonth, getFirstCharacterOfDay } = useComponentComposable();
const { apiOrder } = useViewComposable();
const isPromosConfig = computed(() => props.data.rule.includes('SPECIAL_PROMO_CONFIG_SPECIAL_PROMOS_DESTINATIONS'));

const convertString = (from: string, to: string) => {
  return from || to ? `${t('worksheet.from')} "${handleConvertData(from, props.data.change)}" ${t('worksheet.to')} "${handleConvertData(to, props.data.change)}"` : '';
};

const convertCode = (from: string, to: string) => {
  return from || to ? `${t('worksheet.from')} "${handleConvertCode(from.split(','))}" ${t('worksheet.to')} "${handleConvertCode(to.split(','))}"` : '';
};

const handleConvertCode = (codeConvert: any) => {
  if (codeConvert.includes('EMPTY') || !codeConvert) {
    return codeConvert;
  }
  return props.destCode
    ?.filter((item) => codeConvert.includes(item.code))
    .map((item) => item.airportCode)
    .join(',');
};

const handleConvertData = (data: string, action: string) => {
  if (data === 'EMPTY' || !data) {
    return data;
  }
  switch (action) {
    case 'GROUP_TYPES':
    case 'GROUP_TYPES_HOTEL':
      return handleFormatGroupType(data);
    case 'GUIDED':
      return handleFormatGuided(data, 'guided');
    case 'TOUR_TYPE':
      return handleFormatGuided(data, 'tourType');
    case 'QUOTE_DATE_END':
    case 'QUOTE_DATE_START':
    case 'TRAVEL_DATE_END':
    case 'TRAVEL_DATE_START':
    case 'BLACKOUT_DATE_END':
    case 'BLACKOUT_DATE_START':
    case 'BOOKING_DATE_START':
    case 'BOOKING_DATE_END':
    case 'TRAVEL_DATE_HOTEL_END':
    case 'TRAVEL_DATE_HOTEL_START':
    case 'BLACKOUT_DATE_HOTEL_END':
    case 'BLACKOUT_DATE_HOTEL_START':
    case 'BOOKING_DATE_HOTEL_START':
    case 'BOOKING_DATE_HOTEL_END':
    case 'CREATED_DATE':
    case 'EXPIRY_DATE':
    case 'BOOKING_DATE':
    case 'EXCLUDE_ROOM_CATEGORY_PACKAGE_DATEDEP':
    case 'EXCLUDE_ROOM_CATEGORY_PACKAGE_DATERET':
    case 'EXCLUDE_ROOM_CATEGORY_HOTEL_DATEDEP':
    case 'EXCLUDE_ROOM_CATEGORY_HOTEL_DATERET':
      return handleFormatDate(data);
    case 'ROOM_CATEGORY_CALL_ORDER':
    case 'ROOM_CATEGORY_CALL_ORDER_HOTEL':
    case 'TOUR_API_CALL_ORDER':
      return handleFormatRoomCallOrder(data);
    case 'SEASONAL_SUMMER_STARTMONTH':
    case 'SEASONAL_SUMMER_ENDMONTH':
    case 'SEASONAL_WINTER_STARTMONTH':
    case 'SEASONAL_WINTER_ENDMONTH':
    case 'SEASONAL_EXCEPTION_STARTMONTH':
    case 'SEASONAL_EXCEPTION_ENDMONTH':
      return handleFormatMonthSeasonal(data);
    case 'SEASONAL_SUMMER_DAYOFWEEKS':
    case 'SEASONAL_WINTER_DAYOFWEEKS':
    case 'SEASONAL_EXCEPTION_DAYOFWEEKS':
      return handleFormatDaySeasonal(data);
    default:
      return data;
  }
};

const handleFormatDaySeasonal = (data: string) => {
  const dayData = JSON.parse(data);
  const days = getFirstCharacterOfDay(currentLanguage);
  const formatDay = days.map((day: string, index: number) => {
    return ` ${day} - ${dayData[index] ? t('common.yes') : t('common.no')} `;
  });
  return JSON.stringify(formatDay);
};

const handleFormatMonthSeasonal = (data: string) => {
  const month = renderListMonth(currentLanguage, 'long')[Number(data) - 1];
  return month;
};

const handleFormatRoomCallOrder = (data: string) => {
  return apiOrder.value.find((item: any) => item.value === data)?.label;
};

const handleFormatGuided = (data: string, type: string) => {
  return handleReplaceValueToName(data, type);
};

const handleFormatDate = (data: string) => {
  return formatDate(data, 'dd-LLL-yyyy', true, currentLanguage);
};

const handleFormatGroupType = (data: string) => {
  let cleanedStr = data.replace(/^\[/, '');
  cleanedStr = cleanedStr.replace(/\]$/, '');
  const dataConvert = cleanedStr.split(',').map((item) => item.trim());
  const configList = props.groupTypes?.filter((item: any) => dataConvert?.includes(item.sk));
  const result = configList?.reduce((prev: any, cur: any) => {
    if (!prev) {
      return getTextVariant(currentLanguage, cur.name, cur.nameFr);
    }
    return prev + ', ' + getTextVariant(currentLanguage, cur.name, cur.nameFr);
  }, '');
  return result;
};

const openViewMoreHistory = (from: string, to: string) => {
  const historyChange = {
    oldValue: from,
    newValue: to
  };

  const dialogRef = dialog.open(HistoryViewMoreModal, {
    data: {
      historyChange: historyChange,
      historyRecord: props.data,
      isRuleHistory: true
    },
    props: {
      header: t('management.log.extended_history'),
      modal: true,
      style: {
        width: '75vw'
      }
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};
</script>
<template>
  <div class="change-detail">
    <div v-if="isTermConditionTypes" @click="openViewMoreHistory(data.from, data.to)" class="view-more">
      ...
      <span class="view-more text-blue-600 font-bold underline cursor-pointer text-left uppercase">{{ $t('management.log.view_more') }}</span>
    </div>
    <div v-else>
      <span>{{ isPromosConfig ? convertCode(data.from, data.to) : convertString(data.from, data.to) }}</span>
    </div>
  </div>
</template>
